import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Download } from "lucide-react";

const LINKS = [
  { id: "about", label: "About" },
  { id: "experience", label: "Experience" },
  { id: "projects", label: "Projects" },
  { id: "skills", label: "Skills" },
  { id: "education", label: "Education" },
  { id: "achievements", label: "Achievements" },
  { id: "contact", label: "Contact" },
];

const Navbar = ({ hero = {} }) => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);

    window.addEventListener("scroll", onScroll, {
      passive: true,
    });

    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    const el = document.getElementById(id);

    if (el) {
      el.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }

    setOpen(false);
  };

  const firstName =
    hero.firstName || (hero.name || "").split(" ")[0] || "Portfolio";

  // Resume is served directly from the frontend public folder.
  const resumeLink = "/Soumyajit_Raj_Resume.pdf";

  return (
    <motion.header
      initial={{ y: -40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? "py-3" : "py-5"
      }`}
    >
      <div
        className={`mx-auto max-w-6xl px-5 md:px-8 transition-all duration-500 ${
          scrolled ? "glass rounded-2xl" : "border border-transparent"
        }`}
      >
        <nav className="flex items-center justify-between h-14">
          {/* Logo */}
          <button
            onClick={() => go("hero")}
            className="flex items-center gap-2"
          >
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-cyan-400 to-violet-500 grid place-items-center font-bold text-black">
              {firstName[0]}
            </div>

            <span className="hidden sm:block text-white font-medium">
              {firstName}
              <span className="text-cyan-400">.</span>
            </span>
          </button>

          {/* Desktop Menu */}
          <ul className="hidden md:flex items-center gap-1">
            {LINKS.map((link) => (
              <li key={link.id}>
                <button
                  onClick={() => go(link.id)}
                  className="px-3 py-2 text-[13px] text-slate-400 hover:text-white hover:bg-white/5 rounded-lg transition"
                >
                  {link.label}
                </button>
              </li>
            ))}
          </ul>

          {/* Right Side */}
          <div className="flex items-center gap-2">
            <a
              href={resumeLink}
              download="Soumyajit_Raj_Resume.pdf"
              className="hidden sm:flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-cyan-400 to-violet-500 text-black text-xs font-semibold hover:scale-105 transition"
            >
              <Download size={14} />
              Resume
            </a>

            <button
              type="button"
              onClick={() => setOpen((current) => !current)}
              aria-label={open ? "Close navigation menu" : "Open navigation menu"}
              className="md:hidden p-2 text-white"
            >
              {open ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </nav>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -15 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -15 }}
            className="md:hidden mx-4 mt-3 glass rounded-2xl overflow-hidden"
          >
            <ul className="flex flex-col p-2">
              {LINKS.map((link) => (
                <li key={link.id}>
                  <button
                    type="button"
                    onClick={() => go(link.id)}
                    className="w-full text-left px-4 py-3 text-slate-300 hover:text-white hover:bg-white/5 rounded-lg"
                  >
                    {link.label}
                  </button>
                </li>
              ))}

              <li>
                <a
                  href={resumeLink}
                  download="Soumyajit_Raj_Resume.pdf"
                  onClick={() => setOpen(false)}
                  className="flex items-center gap-2 px-4 py-3 text-cyan-300 hover:bg-white/5 rounded-lg"
                >
                  <Download size={16} />
                  Resume
                </a>
              </li>
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

export default Navbar;